import type {
  CustomizationFontAsset,
  CustomizationImageAsset,
  CustomizationStyleBlock,
  FaviconLinkDescriptor,
  FunnelOverviewCustomization,
  HeroCustomizationAsset,
  ReferencedAssetPolicy,
} from './types';
import type { ProductGalleryImage } from '../molecules/ProductGallery';

const SAFE_TOKEN_NAME = /^--tf-[a-z0-9-]+$/;
const SAFE_CSS_VALUE = /^(#[0-9a-fA-F]{3,8}|rgba?\([0-9,\s.]+\)|hsla?\([0-9,\s.%]+\)|[a-zA-Z0-9\s.,%#-]+)$/;
const SAFE_CSS_SELECTOR = /^(:root|body|\.captured-[a-z0-9_-]+|\.tf-[a-z0-9_-]+|\[data-tf-[a-z0-9_-]+(?:=['"][a-z0-9_-]+['"])?\])$/;
const SAFE_CSS_PROPERTY = /^(--tf-[a-z0-9-]+|[a-z][a-z0-9-]*)$/;
const SAFE_FONT_FORMATS = new Set(['woff2', 'woff', 'otf', 'ttf']);
const SAFE_FONT_STYLES = new Set(['normal', 'italic']);
const SAFE_FONT_DISPLAYS = new Set(['auto', 'block', 'swap', 'fallback', 'optional']);
const SAFE_FONT_FAMILY = /^[a-zA-Z0-9 _-]+$/;
const SAFE_FONT_WEIGHT = /^(normal|bold|[1-9]00)$/;
const BLOCKED_CSS_PATTERN =
  /(<\/?style\b|<script\b|@import\b|@charset\b|expression\s*\(|behavior\s*:|binding\s*:|-moz-binding|url\s*\(|image-set\s*\(|javascript\s*:|\\[0-9a-fA-F]{1,6})/i;

function isSafeUrl(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0 || /['"\\\u0000-\u001f]/.test(trimmed)) return false;
  return trimmed.startsWith('/') || trimmed.startsWith('./') || trimmed.startsWith('../') || trimmed.startsWith('https://');
}

function escapeCssString(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

function isSafeCssValue(value: string): boolean {
  const trimmed = value.trim();
  if (BLOCKED_CSS_PATTERN.test(trimmed)) return false;
  return SAFE_CSS_VALUE.test(trimmed);
}

function isSafeFontWeight(value: string | number | undefined): boolean {
  if (value === undefined) return true;
  return SAFE_FONT_WEIGHT.test(String(value).trim());
}

function isSafeCssDeclaration(declaration: string): boolean {
  const [rawProperty, ...rawValueParts] = declaration.split(':');
  const property = rawProperty?.trim();
  const value = rawValueParts.join(':').trim();
  if (!property || !value) return false;
  if (!SAFE_CSS_PROPERTY.test(property)) return false;
  return isSafeCssValue(value);
}

function isSafeCssRule(rule: string): boolean {
  const match = rule.match(/^\s*([^{}]+)\s*\{\s*([^{}]+)\s*\}\s*$/);
  if (!match) return false;

  const selectors = match[1].split(',').map((selector) => selector.trim());
  if (selectors.length === 0 || selectors.some((selector) => !SAFE_CSS_SELECTOR.test(selector))) {
    return false;
  }

  const declarations = match[2]
    .split(';')
    .map((declaration) => declaration.trim())
    .filter(Boolean);

  return declarations.length > 0 && declarations.every(isSafeCssDeclaration);
}

function isSafeCssOverride(css: string): boolean {
  const trimmed = css.trim();
  if (!trimmed || BLOCKED_CSS_PATTERN.test(trimmed)) return false;

  const rules = trimmed.match(/[^{}]+\{[^{}]+\}/g);
  if (!rules || rules.join('').replace(/\s/g, '') !== trimmed.replace(/\s/g, '')) return false;
  return rules.every(isSafeCssRule);
}

function buildFontFace(font: CustomizationFontAsset): string | null {
  const format = font.format?.trim();
  const style = font.style ?? 'normal';
  const display = font.display ?? 'swap';

  if (
    !SAFE_FONT_FAMILY.test(font.family) ||
    !isSafeUrl(font.src) ||
    !isSafeFontWeight(font.weight) ||
    !SAFE_FONT_STYLES.has(style) ||
    !SAFE_FONT_DISPLAYS.has(display) ||
    (format !== undefined && !SAFE_FONT_FORMATS.has(format))
  ) {
    return null;
  }

  const declarations = [
    `font-family: '${escapeCssString(font.family)}';`,
    `src: url('${escapeCssString(font.src.trim())}')${format ? ` format('${format}')` : ''};`,
    `font-weight: ${font.weight ?? 'normal'};`,
    `font-style: ${style};`,
    `font-display: ${display};`,
  ];

  return `@font-face {\n  ${declarations.join('\n  ')}\n}`;
}

function buildFontCss(fonts: readonly CustomizationFontAsset[] | undefined): string {
  return (fonts ?? [])
    .map(buildFontFace)
    .filter((css): css is string => css !== null)
    .join('\n\n');
}

function buildTokenCss(tokens: Record<string, string> | undefined): string {
  const declarations = Object.entries(tokens ?? {})
    .filter(([name, value]) => SAFE_TOKEN_NAME.test(name) && isSafeCssValue(value))
    .map(([name, value]) => `  ${name}: ${value.trim()};`);

  if (declarations.length === 0) return '';

  return `:root {\n${declarations.join('\n')}\n}`;
}

function buildOverridesCss(overrides: readonly string[] | undefined): string {
  return (overrides ?? [])
    .filter(isSafeCssOverride)
    .join('\n\n');
}

export function buildCustomizationStyleBlocks(
  customization: FunnelOverviewCustomization | undefined,
): CustomizationStyleBlock[] {
  if (!customization) return [];

  const blocks: CustomizationStyleBlock[] = [];
  const fontCss = buildFontCss(customization.fonts);
  const tokenCss = buildTokenCss(customization.colorTokens);
  const overridesCss = buildOverridesCss(customization.cssOverrides);

  if (fontCss) {
    blocks.push({ slotId: 'theme.fonts', css: fontCss });
  }

  if (tokenCss) {
    blocks.push({ slotId: 'theme.colorTokens', css: tokenCss });
  }

  if (overridesCss) {
    blocks.push({ slotId: 'theme.cssOverrides', css: overridesCss });
  }

  return blocks;
}

export function normalizeProductGalleryAssets(
  assets: readonly CustomizationImageAsset[] | undefined,
  { allowReferencedAssets = false }: ReferencedAssetPolicy = {},
): ProductGalleryImage[] {
  return (assets ?? [])
    .filter((asset) => asset.licenseRisk !== 'blocked')
    .filter((asset) => asset.source !== 'referenced' || allowReferencedAssets)
    .filter((asset) => asset.src.trim().length > 0)
    .map((asset) => ({
      key: asset.assetId,
      url: asset.src,
      alt: asset.alt,
      width: asset.width,
      height: asset.height,
    }));
}

export function buildFaviconLinkDescriptors(
  assets: readonly CustomizationImageAsset[] | undefined,
): FaviconLinkDescriptor[] {
  return (assets ?? [])
    .filter((asset) => asset.licenseRisk !== 'blocked')
    .filter((asset) => asset.source !== 'referenced')
    .map((asset) => ({
      rel: asset.width === 180 || asset.height === 180 ? 'apple-touch-icon' : 'icon',
      href: asset.src,
      sizes: asset.width && asset.height ? `${asset.width}x${asset.height}` : undefined,
      type: asset.src.endsWith('.svg') ? 'image/svg+xml' : undefined,
    }));
}

export function resolveHeroCustomizationAsset(
  customization: Pick<FunnelOverviewCustomization, 'heroAsset' | 'videoPoster'> | undefined,
  { allowReferencedAssets = false }: ReferencedAssetPolicy = {},
): HeroCustomizationAsset | undefined {
  if (!customization?.heroAsset) return undefined;

  const { heroAsset, videoPoster } = customization;
  if (
    heroAsset.kind !== 'video' ||
    heroAsset.poster ||
    videoPoster?.licenseRisk === 'blocked' ||
    (videoPoster?.source === 'referenced' && !allowReferencedAssets)
  ) {
    return heroAsset;
  }

  return {
    ...heroAsset,
    poster: videoPoster?.src,
  };
}
